/**
 * SubscriptionSelector Custom Element
 * Manages modern subscription cards, frequency selection, dynamic price updates,
 * and Shopify form synchronization for Michelle Peiret Health.
 */
(() => {
  if (customElements.get('subscription-selector')) return;

  class SubscriptionSelector extends HTMLElement {
  constructor() {
    super();
    this.data = null;
    this.selectedOption = 'subscription';
    this.selectedPlanId = '';
    this.currentVariant = null;
  }

  connectedCallback() {
    this.initData();
    this.cacheElements();
    this.bindEvents();
    this.syncInitialState();
  }

  initData() {
    const dataScript = this.querySelector('.subscription-data-json');
    if (!dataScript) return;

    try {
      this.data = JSON.parse(dataScript.textContent);
      this.selectedOption = this.dataset.selected || 'subscription';
      this.selectedPlanId = this.dataset.defaultPlanId || '';

      if (this.data.variants && this.data.variants.length > 0) {
        this.currentVariant =
          this.data.variants.find((v) => String(v.id) === String(this.data.currentVariantId)) ||
          this.data.variants[0];
      }
    } catch (e) {
      console.error('[SubscriptionSelector] Error parsing JSON data:', e);
    }
  }

  cacheElements() {
    this.sellingPlanInput = this.querySelector('.subscription-selling-plan-input');
    this.subCard = this.querySelector('.sub-card--subscription');
    this.onetimeCard = this.querySelector('.sub-card--onetime');
    this.cards = this.querySelectorAll('.sub-card');
    this.freqSelect = this.querySelector('[data-frequency-select]');
    this.subPriceEl = this.querySelector('[data-sub-price]');
    this.subComparePriceEl = this.querySelector('[data-sub-compare-price]');
    this.onetimePriceEl = this.querySelector('[data-onetime-price]');
    this.onetimeComparePriceEl = this.querySelector('[data-onetime-compare-price]');
    this.closestSection = this.closest('.shopify-section') || document;
  }

  bindEvents() {
    // Card clicks
    this.cards.forEach((card) => {
      card.addEventListener('click', (e) => {
        // Prevent click if clicking inside frequency select
        if (e.target.closest('[data-frequency-select]')) return;
        const type = card.dataset.optionType;
        this.selectOption(type);
      });

      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const type = card.dataset.optionType;
          this.selectOption(type);
        }
      });
    });

    // Frequency select change
    if (this.freqSelect) {
      this.freqSelect.addEventListener('change', (e) => {
        this.selectedPlanId = e.target.value;
        if (this.selectedOption === 'subscription') {
          this.updateSellingPlanInput();
          this.updatePrices();
        }
      });
    }

    // Listen for variant update events from the theme
    this.closestSection.addEventListener('variant:update', this.onVariantUpdate.bind(this));
    document.addEventListener('variant:update', this.onVariantUpdate.bind(this));

    // Ensure hidden input is inside product form
    this.ensureInputInForm();
  }

  ensureInputInForm() {
    const form = this.closest('form') || document.querySelector(`form#${this.sellingPlanInput?.getAttribute('form')}`);
    if (form && this.sellingPlanInput && !form.contains(this.sellingPlanInput)) {
      form.appendChild(this.sellingPlanInput);
    }
  }

  syncInitialState() {
    this.selectOption(this.selectedOption, true);
    // Slight delay to allow theme price component to initialize before syncing
    setTimeout(() => {
      this.syncMainProductPrice();
    }, 100);
  }

  selectOption(type, isInit = false) {
    this.selectedOption = type;
    this.dataset.selected = type;

    if (type === 'subscription') {
      this.subCard?.classList.add('is-selected');
      this.subCard?.setAttribute('aria-checked', 'true');
      this.onetimeCard?.classList.remove('is-selected');
      this.onetimeCard?.setAttribute('aria-checked', 'false');

      if (!this.selectedPlanId && this.freqSelect) {
        this.selectedPlanId = this.freqSelect.value;
      }
      this.updateSellingPlanInput();
    } else {
      this.onetimeCard?.classList.add('is-selected');
      this.onetimeCard?.setAttribute('aria-checked', 'true');
      this.subCard?.classList.remove('is-selected');
      this.subCard?.setAttribute('aria-checked', 'false');

      if (this.sellingPlanInput) {
        this.sellingPlanInput.value = '';
      }
    }

    if (!isInit) {
      this.syncMainProductPrice();
    }
  }

  updateSellingPlanInput() {
    if (this.sellingPlanInput) {
      this.sellingPlanInput.value = this.selectedOption === 'subscription' ? this.selectedPlanId : '';
    }
  }

  onVariantUpdate(event) {
    const newVariant = event.detail?.resource;
    if (!newVariant || !this.data) return;

    this.currentVariant =
      this.data.variants.find((v) => String(v.id) === String(newVariant.id)) || newVariant;

    this.updatePrices();
    this.syncMainProductPrice();
  }

  getCurrentPrices() {
    if (!this.currentVariant) return null;

    const onetimePrice = this.currentVariant.price;
    const onetimeCompare = this.currentVariant.compare_at_price;

    let subPrice = onetimePrice;
    let subCompare = onetimeCompare || onetimePrice;

    if (this.currentVariant.selling_plan_allocations) {
      const allocation = this.currentVariant.selling_plan_allocations.find(
        (a) => String(a.selling_plan_id) === String(this.selectedPlanId)
      );

      if (allocation) {
        subPrice = allocation.price;
        subCompare = allocation.compare_at_price || onetimePrice;
      } else if (this.data.sellingPlanGroups?.[0]?.selling_plans?.[0]) {
        const plan = this.data.sellingPlanGroups[0].selling_plans[0];
        const adj = plan.price_adjustments?.[0];
        if (adj?.value_type === 'percentage') {
          subPrice = Math.round(onetimePrice * (1 - adj.value / 100));
        }
      }
    }

    return {
      onetimePrice,
      onetimeCompare,
      subPrice,
      subCompare,
    };
  }

  updatePrices() {
    const prices = this.getCurrentPrices();
    if (!prices) return;

    if (this.subPriceEl) {
      this.subPriceEl.textContent = this.formatMoney(prices.subPrice);
    }
    if (this.subComparePriceEl) {
      if (prices.subCompare > prices.subPrice) {
        this.subComparePriceEl.textContent = this.formatMoney(prices.subCompare);
        this.subComparePriceEl.style.display = '';
      } else {
        this.subComparePriceEl.style.display = 'none';
      }
    }

    if (this.onetimePriceEl) {
      this.onetimePriceEl.textContent = this.formatMoney(prices.onetimePrice);
    }
    if (this.onetimeComparePriceEl) {
      if (prices.onetimeCompare > prices.onetimePrice) {
        this.onetimeComparePriceEl.textContent = this.formatMoney(prices.onetimeCompare);
        this.onetimeComparePriceEl.style.display = '';
      } else {
        this.onetimeComparePriceEl.style.display = 'none';
      }
    }
  }

  syncMainProductPrice() {
    const priceBlock = document.querySelector('product-price');
    if (!priceBlock) return;

    const prices = this.getCurrentPrices();
    if (!prices) return;

    const priceContainer = priceBlock.querySelector('.price-container');
    const regularContainer = priceBlock.querySelector('.price__regular');
    const saleContainer = priceBlock.querySelector('.price__sale');
    const salePriceEl = priceBlock.querySelector('.price--sale');
    const regularPriceEl = priceBlock.querySelector('.price__regular .price');
    const compareAtEl = priceBlock.querySelector('.compare-at-price');

    if (this.selectedOption === 'subscription' && prices.subPrice < prices.onetimePrice) {
      // Show subscription discounted price as sale price
      if (priceContainer) priceContainer.classList.add('price--on-sale');
      if (regularContainer) regularContainer.style.display = 'none';
      if (saleContainer) saleContainer.style.display = 'block';

      if (salePriceEl) {
        salePriceEl.textContent = this.formatMoney(prices.subPrice);
      }
      if (compareAtEl) {
        compareAtEl.textContent = this.formatMoney(prices.onetimePrice);
        compareAtEl.style.display = 'inline';
      }
    } else {
      // Restore standard price
      const hasNaturalCompare = prices.onetimeCompare && prices.onetimeCompare > prices.onetimePrice;

      if (hasNaturalCompare) {
        if (priceContainer) priceContainer.classList.add('price--on-sale');
        if (regularContainer) regularContainer.style.display = 'none';
        if (saleContainer) saleContainer.style.display = 'block';
        if (salePriceEl) salePriceEl.textContent = this.formatMoney(prices.onetimePrice);
        if (compareAtEl) {
          compareAtEl.textContent = this.formatMoney(prices.onetimeCompare);
          compareAtEl.style.display = 'inline';
        }
      } else {
        if (priceContainer) priceContainer.classList.remove('price--on-sale');
        if (regularContainer) regularContainer.style.display = 'block';
        if (saleContainer) saleContainer.style.display = 'none';
        if (regularPriceEl) regularPriceEl.textContent = this.formatMoney(prices.onetimePrice);
      }
    }
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney && this.data?.moneyFormat) {
      return Shopify.formatMoney(cents, this.data.moneyFormat);
    }
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }
}

  if (!customElements.get('subscription-selector')) {
    customElements.define('subscription-selector', SubscriptionSelector);
  }
})();

